/** @format */
import { centToDecimal, decimalToCent } from '@toucan/client/global/currency';
import { Model } from '@toucan/client/global/model';
import {
    AttributeKey,
    InternalMediaServiceImage,
    LegacyProductImage,
    Observation,
    PriceModifier,
    ProductState,
    ProductSummaryV2,
    productConditionStringToEnum,
} from '@toucan/client/global/product';

import { CatalogueModelPriceModifierNode } from '../model/types';
import { getMissingCorePriceModifiers } from './helpers';
import {
    InventoryAttributeNode,
    InventoryImageNode,
    InventoryProductNode,
    ProductLookupProduct,
    ProductSummaryResponse,
} from './types';

export function transformProductIdFromSkuResponse(
    ProductIdFromSkuResponse: ProductLookupProduct
): number | undefined {
    return ProductIdFromSkuResponse.id;
}

export function transformProductSummaryImagesResponse(
    images: InternalMediaServiceImage[]
): LegacyProductImage[] {
    return images.map(({ alt, url, fid, index }) => ({
        alt,
        url,
        product: '',
        image: fid,
        imageUrl: url,
        thumb: url,
        ordering: index,
    }));
}

export function transformProductSummaryResponse(
    {
        discount,
        listPrice,
        condition,
        name,
        modelName,
        state,
        images,
        internalImages,
        ...productSummaryResponse
    }: ProductSummaryResponse,
    allowUnknownCondition: boolean = false
): ProductSummaryV2 {
    const price = listPrice ?? 0;
    return {
        ...productSummaryResponse,
        state,
        modelName,
        discount: discount?.toString(),
        listPrice: price.toString(),
        value: decimalToCent(price),
        condition: productConditionStringToEnum(
            condition,
            allowUnknownCondition
        ),
        name: name || modelName,
        isSold: state === ProductState.SHIPPED,
        images: transformProductSummaryImagesResponse(images ?? []),
        internalImages: transformProductSummaryImagesResponse(
            internalImages ?? []
        ),
    };
}

export function transformInventoryResponse(
    {
        product,
        model,
        modelPriceModifiers,
        observations,
        translationSlug,
        conditionDescription,
    }: {
        product: InventoryProductNode;
        model: Model;
        modelPriceModifiers: CatalogueModelPriceModifierNode[];
        observations: Observation[];
        translationSlug: string;
        conditionDescription: string;
    },
    allowUnknownCondition: boolean = false
): ProductSummaryV2 {
    const price = product.price?.total ?? 0;
    const originalPrice = product.price?.minusDiscount;

    const condition = transformInventoryCondition(
        product.condition ?? null,
        product.productId
    );

    const attributes = transformInventoryAttributes(product.attributes ?? []);

    const priceModifiers = transformInventoryPriceModifiers(
        product.priceModifiers ?? [],
        modelPriceModifiers,
        translationSlug
    );

    const publicImages = (product.images ?? []).filter(
        (image) => !image.isInternal
    );
    const internalImages = (product.images ?? []).filter(
        (image) => image.isInternal
    );

    return {
        ...model,
        sku: product.sku ?? '',
        discountIsPercentage: product.discounts?.[0]?.isPercentage ?? false,
        fullSku: product.sku ?? '',
        id: product.productId,
        observations,
        primaryCategoryName: '',
        description: model.translations?.[0]?.description ?? '',
        hasCustomDescription: product.hasCustomDescription,
        conditionDescription,
        customDescription: product.customDescription ?? '',
        priceModifiers,
        productPriceOriginal:
            originalPrice != null
                ? centToDecimal(originalPrice).toString()
                : '',
        attributes,
        state: product.state as ProductState,
        modelName: model.translations?.[0]?.name ?? '',
        discount: product.discounts?.[0]?.absoluteValue?.toString() ?? '',
        listPrice: price != null ? centToDecimal(price).toString() : '',
        value: price,
        condition: productConditionStringToEnum(
            condition,
            allowUnknownCondition
        ),
        name: model.translations?.[0]?.name ?? '',
        isSold: (product.state as ProductState) === ProductState.SHIPPED,
        images: transformInventoryImages(publicImages, translationSlug),
        internalImages: transformInventoryImages(
            internalImages,
            translationSlug
        ),
        modelId: model.id,
    };
}

export function transformInventoryAttributes(
    attributes: InventoryAttributeNode[]
): { name: AttributeKey; content: string }[] {
    return attributes.map((attribute) => ({
        name: attribute.name as AttributeKey,
        content: attribute.value ?? '',
    }));
}

export function transformInventoryCondition(
    condition: { name: string; slug: string } | null,
    productId: number
): string {
    if (!condition) {
        throw new Error(
            `Product ${productId} returned from inventory with no condition`
        );
    }
    return condition.name;
}

export function transformInventoryImages(
    images: InventoryImageNode[],
    translationSlug: string
): LegacyProductImage[] {
    return images.map((image, index) => {
        const { alt = '' } =
            image.translations.find(
                (translation) => translation.languageCode === translationSlug
            ) ?? {};
        return {
            alt,
            url: image.url,
            product: '',
            image: image.fileId,
            imageUrl: image.url,
            thumb: image.url,
            ordering: index,
        };
    });
}

export function transformCataloguePriceModifier(
    modelModifier: CatalogueModelPriceModifierNode,
    translationSlug: string,
    present: boolean
): PriceModifier {
    return {
        id: modelModifier.priceModifier.priceModifierId,
        name:
            modelModifier.priceModifier.translations.find(
                (translation) => translation.languageCode === translationSlug
            )?.name ?? '',
        core: modelModifier.isCore,
        essential: modelModifier.isEssential,
        present,
    };
}

export function transformInventoryPriceModifiers(
    presentModifiers: { priceModifierId: number }[],
    modelModifiers: CatalogueModelPriceModifierNode[],
    translationSlug: string
): PriceModifier[] {
    const presentModelModifiers = presentModifiers
        .map(({ priceModifierId }) =>
            modelModifiers.find(
                (modelModifier) =>
                    modelModifier.priceModifier.priceModifierId ===
                    priceModifierId
            )
        )
        .filter((modelModifier) => modelModifier !== undefined);

    const missingCoreModifiers = getMissingCorePriceModifiers(
        modelModifiers,
        presentModifiers
    );

    return [
        ...presentModelModifiers.map((modelModifier) =>
            transformCataloguePriceModifier(
                modelModifier,
                translationSlug,
                true
            )
        ),
        ...missingCoreModifiers.map((modelModifier) =>
            transformCataloguePriceModifier(
                modelModifier,
                translationSlug,
                false
            )
        ),
    ];
}
